import React, { FC, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as CheckIcon } from 'studio_react/svg/common/check.svg';
import { ReactComponent as ChevronLeftIcon } from 'studio_react/svg/common/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from 'studio_react/svg/common/chevron-right.svg';
import { ReactComponent as FastForwardIcon } from 'studio_react/svg/common/fast-forward.svg';
import { ReactComponent as SlidersIcon } from 'studio_react/svg/common/sliders.svg';
import { useTranslate } from 'ts/common/hooks';

const SPEEDS = [0.5, 1, 1.25, 1.5, 2];
const QUALITIES = ['Auto', '360p', '720p', '1080p', '1440p', '4k'];

const speedLabel = (s: number, normal: string) => (s === 1 ? normal : `${s}x`);

interface Props {
    playbackSpeed: number;
    quality: string;
    onSpeedChange: (speed: number) => void;
    onQualityChange: (quality: string) => void;
}

type Panel = null | 'speed' | 'quality';

const rowStyles = {
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    px: 2,
    py: '8px',
    rounded: '4px',
    cursor: 'pointer',
    _hover: { bg: 'whiteAlpha.100' }
};

const VideoSettings: FC<Props> = ({ playbackSpeed, quality, onSpeedChange, onQualityChange }) => {
    const t = useTranslate('studio.common.videoPlayer');
    const [panel, setPanel] = useState<Panel>(null);

    const panelContainer = (children: React.ReactNode) => (
        <Box
            bg="rgba(0,0,0,0.6)"
            rounded="4px"
            p={4}
            w="350px"
            display="flex"
            flexDirection="column"
            gap={1}
        >
            {children}
        </Box>
    );

    const backRow = (label: string) => (
        <Flex
            {...rowStyles}
            mb={1}
            onClick={() => setPanel(null)}
            _hover={{ bg: 'whiteAlpha.100' }}
        >
            <Flex gap={2} alignItems="center">
                <ChevronLeftIcon width={20} height={20} />
                <Text color="white" fontSize="16px" fontWeight="medium">
                    {label}
                </Text>
            </Flex>
        </Flex>
    );

    if (panel === 'speed') {
        return panelContainer(
            <>
                {backRow(t('playbackSpeed'))}
                {SPEEDS.map((s) => (
                    <Flex
                        key={s}
                        {...rowStyles}
                        bg={s === playbackSpeed ? 'rgba(0,0,0,0.6)' : 'transparent'}
                        onClick={() => {
                            onSpeedChange(s);
                            setPanel(null);
                        }}
                    >
                        <Text color="white" fontSize="16px">
                            {speedLabel(s, t('normal'))}
                        </Text>
                        {s === playbackSpeed && <CheckIcon width={16} height={16} />}
                    </Flex>
                ))}
            </>
        );
    }

    if (panel === 'quality') {
        return panelContainer(
            <>
                {backRow(t('quality'))}
                {QUALITIES.map((q) => (
                    <Flex
                        key={q}
                        {...rowStyles}
                        bg={q === quality ? 'rgba(0,0,0,0.6)' : 'transparent'}
                        onClick={() => {
                            onQualityChange(q);
                            setPanel(null);
                        }}
                    >
                        <Text color="white" fontSize="16px">
                            {q}
                        </Text>
                        {q === quality && <CheckIcon width={16} height={16} />}
                    </Flex>
                ))}
            </>
        );
    }

    return panelContainer(
        <>
            {/* Playback speed row */}
            <Flex {...rowStyles} bg="rgba(0,0,0,0.6)" onClick={() => setPanel('speed')}>
                <Flex gap={3} alignItems="center">
                    <FastForwardIcon width={24} height={24} />
                    <Text color="white" fontSize="16px">
                        {t('playbackSpeed')}
                    </Text>
                </Flex>
                <Flex gap={3} alignItems="center">
                    <Text color="white" fontSize="14px">
                        {speedLabel(playbackSpeed, t('normal'))}
                    </Text>
                    <ChevronRightIcon width={24} height={24} />
                </Flex>
            </Flex>

            {/* Quality row */}
            <Flex {...rowStyles} onClick={() => setPanel('quality')}>
                <Flex gap={3} alignItems="center">
                    <SlidersIcon width={24} height={24} />
                    <Text color="white" fontSize="16px">
                        {t('quality')}
                    </Text>
                </Flex>
                <Flex gap={3} alignItems="center">
                    <Text color="white" fontSize="14px">
                        {quality}
                    </Text>
                    <ChevronRightIcon width={24} height={24} />
                </Flex>
            </Flex>
        </>
    );
};

export default VideoSettings;
